import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { moderationApi } from '@/lib/api'
import { toast } from '@/components/ui/use-toast'

interface ReportModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  contentType: 'post' | 'comment' | 'user' | 'message'
  contentId: string
  authorUsername?: string
}

const REPORT_REASONS = [
  { value: 'spam', label: 'Spam or misleading' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'hate_speech', label: 'Hate speech or symbols' },
  { value: 'violence', label: 'Violence or dangerous content' },
  { value: 'nudity', label: 'Nudity or sexual content' },
  { value: 'self_harm', label: 'Self-harm or suicide' },
  { value: 'misinformation', label: 'False information' },
  { value: 'intellectual_property', label: 'Intellectual property violation' },
  { value: 'other', label: 'Something else' },
]

export function ReportModal({
  open,
  onOpenChange,
  contentType,
  contentId,
  authorUsername,
}: ReportModalProps) {
  const [selectedReason, setSelectedReason] = useState<string>('')
  const [description, setDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const reportMutation = useMutation({
    mutationFn: ({
      contentType,
      contentId,
      reason,
      description,
    }: {
      contentType: string
      contentId: string
      reason: string
      description?: string
    }) => moderationApi.reportContent(contentType, contentId, reason, description),
    onSuccess: () => {
      toast({
        title: 'Report submitted',
        description: 'Thank you for helping keep KLASSIC safe. We will review this report.',
      })
      onOpenChange(false)
      // Reset form
      setSelectedReason('')
      setDescription('')
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to submit report',
        description: error.message || 'An error occurred while submitting the report',
        variant: 'destructive',
      })
    },
    onSettled: () => {
      setIsSubmitting(false)
    },
  })

  const handleSubmit = () => {
    if (!selectedReason) {
      toast({
        title: 'Reason required',
        description: 'Please select a reason for reporting',
        variant: 'destructive',
      })
      return
    }

    setIsSubmitting(true)
    reportMutation.mutate({
      contentType,
      contentId,
      reason: selectedReason,
      description: description.trim() || undefined,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Report {contentType}</DialogTitle>
          <DialogDescription>
            {authorUsername
              ? `Why are you reporting this ${contentType} from @${authorUsername}?`
              : `Why are you reporting this ${contentType}?`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Reason selection */}
          <div className="space-y-2">
            <Label>Reason for reporting</Label>
            <div className="space-y-2">
              {REPORT_REASONS.map((reason) => (
                <label
                  key={reason.value}
                  className="flex cursor-pointer items-center space-x-3 rounded-lg border p-3 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <input
                    type="radio"
                    name="reason"
                    value={reason.value}
                    checked={selectedReason === reason.value}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="h-4 w-4 border-gray-300 text-primary focus:ring-primary"
                    disabled={isSubmitting}
                  />
                  <span className="text-sm">{reason.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Additional description */}
          <div className="space-y-2">
            <Label htmlFor="report-description">
              Additional information (optional)
            </Label>
            <Textarea
              id="report-description"
              placeholder="Provide more details about why you're reporting this..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="resize-none"
              rows={3}
              disabled={isSubmitting}
            />
          </div>

          {/* Warning message */}
          <p className="text-xs text-muted-foreground">
            Reports are anonymous. If someone is in immediate danger, call local emergency
            services. Don't wait.
          </p>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !selectedReason}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Submit report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
